import React, { useState, useEffect, useRef, useCallback } from 'react';

const C = {
  navy: '#0e225d',
  navyMid: '#1a3370',
  navyHover: 'rgba(14,34,93,0.06)',
  gold: '#fbb21f',
  goldDim: 'rgba(251,178,31,0.14)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white: '#ffffff',
  border: '#e5e7eb',
  muted: '#9ca3af',
  sub: '#6b7280',
  body: '#1f2937',
  bg: '#f7f8fa',
  green: '#16a34a',
  greenDim: 'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red: '#dc2626',
  redDim: 'rgba(220,38,38,0.08)',
  redBorder: 'rgba(220,38,38,0.25)',
};

interface Question {
  question_id: string;
  question_text: string;
  question_type: 'mcq' | 'true_false' | 'short';
  options: string[];
  marks: number;
  section: string;
}

interface Exam {
  exam_title: string;
  subject: string;
  total_marks: number;
  duration_minutes: number;
  questions: Question[];
}

interface Result {
  obtained: number;
  total: number;
  answered: number;
  skipped: number;
  total_questions: number;
}

const DUMMY_EXAM: Exam = {
  exam_title: 'Mathematics — Class 10 Mid-Term',
  subject: 'Mathematics',
  total_marks: 80,
  duration_minutes: 90,
  questions: [
    { question_id: 'q1',  question_text: 'If α and β are the roots of x² − 5x + 6 = 0, find the value of α² + β².', question_type: 'mcq',        options: ['13', '7', '25', '11'],                      marks: 2, section: 'Algebra' },
    { question_id: 'q2',  question_text: 'A die is thrown once. What is the probability of getting a prime number?',  question_type: 'mcq',        options: ['1/2', '1/3', '2/3', '1/6'],                 marks: 2, section: 'Probability' },
    { question_id: 'q3',  question_text: 'The HCF of 96 and 404 is 4. Find their LCM.',                               question_type: 'mcq',        options: ['9696', '4848', '9692', '19192'],            marks: 2, section: 'Number Theory' },
    { question_id: 'q4',  question_text: 'If tan θ = 4/3, what is the value of sin θ + cos θ?',                       question_type: 'mcq',        options: ['7/5', '1', '12/5', '11/5'],                 marks: 2, section: 'Trigonometry' },
    { question_id: 'q5',  question_text: 'The sum of first 20 natural numbers is:',                                   question_type: 'mcq',        options: ['190', '200', '210', '220'],                 marks: 2, section: 'Arithmetic' },
    { question_id: 'q6',  question_text: 'The distance between points (3, 4) and (−3, −4) is:',                      question_type: 'mcq',        options: ['10', '8', '14', '5'],                       marks: 2, section: 'Coordinate' },
    { question_id: 'q7',  question_text: 'Solve: 2x + 3 = 11. What is x?',                                           question_type: 'mcq',        options: ['3', '4', '5', '7'],                         marks: 2, section: 'Algebra' },
    { question_id: 'q8',  question_text: 'Which of the following is irrational?',                                     question_type: 'mcq',        options: ['√9', '√16', '√7', '√25'],                  marks: 2, section: 'Number Theory' },
    { question_id: 'q9',  question_text: 'A quadratic polynomial with zeroes 3 and −2 is always divisible by x. True or False?', question_type: 'true_false', options: ['True', 'False'],                marks: 1, section: 'Algebra' },
    { question_id: 'q10', question_text: 'tan(90°) is defined. True or False?',                                       question_type: 'true_false', options: ['True', 'False'],                            marks: 1, section: 'Trigonometry' },
    { question_id: 'q11', question_text: 'In an AP, a = 2, d = 3. Find the 10th term.',                               question_type: 'mcq',        options: ['29', '28', '30', '32'],                     marks: 2, section: 'Arithmetic' },
    { question_id: 'q12', question_text: 'If cos A = 12/13, find sin A.',                                             question_type: 'mcq',        options: ['5/13', '7/13', '1/13', '12/13'],           marks: 2, section: 'Trigonometry' },
    { question_id: 'q13', question_text: 'What is the value of sin²30° + cos²60°?',                                   question_type: 'mcq',        options: ['1', '1/2', '3/4', '2'],                    marks: 2, section: 'Trigonometry' },
    { question_id: 'q14', question_text: 'The mean of 5 numbers is 18. If one number is removed, the mean becomes 16. Find the removed number.', question_type: 'mcq', options: ['26', '24', '28', '22'], marks: 2, section: 'Statistics' },
    { question_id: 'q15', question_text: 'Area of triangle with vertices (0,0), (4,0), (0,3) is:',                    question_type: 'mcq',        options: ['6', '12', '7', '10'],                       marks: 2, section: 'Coordinate' },
    { question_id: 'q16', question_text: 'The discriminant of 2x²−4x+3 = 0 is:',                                     question_type: 'mcq',        options: ['−8', '8', '16', '−16'],                    marks: 2, section: 'Algebra' },
    { question_id: 'q17', question_text: 'Tangent to a circle is perpendicular to the radius at the point of contact. True or False?', question_type: 'true_false', options: ['True', 'False'],          marks: 1, section: 'Geometry' },
    { question_id: 'q18', question_text: 'sin(90°−θ) equals:',                                                        question_type: 'mcq',        options: ['cos θ', 'sin θ', 'tan θ', 'cot θ'],        marks: 2, section: 'Trigonometry' },
    { question_id: 'q19', question_text: 'If P(E) = 0.7, then P(not E) =',                                            question_type: 'mcq',        options: ['0.3', '0.7', '1', '0'],                    marks: 2, section: 'Probability' },
    { question_id: 'q20', question_text: 'Explain in your own words what the Pythagorean theorem states and give one real-life application.', question_type: 'short', options: [],                       marks: 4, section: 'Geometry' },
  ],
};

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E'];

const fmtTime = (secs: number): string => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const TakeExam: React.FC = () => {
  const exam      = DUMMY_EXAM;
  const questions = exam.questions;

  const [currentIdx, setCurrentIdx]   = useState<number>(0);
  const [answers, setAnswers]         = useState<Record<string, string>>({});
  const [flags, setFlags]             = useState<Set<string>>(new Set());
  const [timeLeft, setTimeLeft]       = useState<number>(exam.duration_minutes * 60);
  const [shortDraft, setShortDraft]   = useState<string>('');
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [submitted, setSubmitted]     = useState<boolean>(false);
  const [result, setResult]           = useState<Result | null>(null);

  const timerRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const answersRef  = useRef<Record<string, string>>(answers);

  useEffect(() => { answersRef.current = answers; }, [answers]);

  const buildResult = useCallback((finalAnswers: Record<string, string>): Result => ({
    obtained:        Object.keys(finalAnswers).length * 2,
    total:           exam.total_marks,
    answered:        Object.keys(finalAnswers).length,
    skipped:         questions.length - Object.keys(finalAnswers).length,
    total_questions: questions.length,
  }), [exam.total_marks, questions.length]);

  const handleAutoSubmit = useCallback(() => {
    const current = answersRef.current;
    setResult(buildResult(current));
    setSubmitted(true);
  }, [buildResult]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [handleAutoSubmit]);

  const saveShortDraft = useCallback(() => {
    const q = questions[currentIdx];
    if (q.question_type === 'short' && shortDraft.trim()) {
      setAnswers(prev => ({ ...prev, [q.question_id]: shortDraft.trim() }));
    }
  }, [questions, currentIdx, shortDraft]);

  useEffect(() => {
    const q = questions[currentIdx];
    if (q.question_type === 'short') {
      setShortDraft(answers[q.question_id] ?? '');
    } else {
      setShortDraft('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx]);

  const goTo = useCallback((idx: number) => {
    saveShortDraft();
    setCurrentIdx(idx);
  }, [saveShortDraft]);

  const setAnswer = (qid: string, value: string) =>
    setAnswers(prev => ({ ...prev, [qid]: value }));

  const clearAnswer = (qid: string) => {
    setAnswers(prev => { const n = { ...prev }; delete n[qid]; return n; });
    setShortDraft('');
  };

  const toggleFlag = (qid: string) => {
    setFlags(prev => {
      const n = new Set(prev);
      n.has(qid) ? n.delete(qid) : n.add(qid);
      return n;
    });
  };

  const handleSubmit = () => {
    saveShortDraft();
    if (timerRef.current) clearInterval(timerRef.current);
    setAnswers(prev => {
      setResult(buildResult(prev));
      return prev;
    });
    setSubmitted(true);
    setShowConfirm(false);
  };

  const isWarning  = timeLeft <= 300 && timeLeft > 0;
  const isCritical = timeLeft <= 60  && timeLeft > 0;

  const answeredCount = Object.keys(answers).length;
  const unanswered    = questions.length - answeredCount;

  const dotStyle = (idx: number): React.CSSProperties => {
    const qid   = questions[idx].question_id;
    const isCur = idx === currentIdx;
    const ans   = answers[qid] !== undefined;
    const flag  = flags.has(qid);
    if (isCur) return { background: C.navy,     color: C.white,   borderColor: C.navy };
    if (flag)  return { background: '#fffbeb',  color: '#92400e', borderColor: '#fcd34d' };
    if (ans)   return { background: C.greenDim, color: '#166534', borderColor: '#86efac' };
    return       { background: C.white, color: C.sub, borderColor: C.border };
  };

  /* ── RESULT SCREEN ── */
  if (submitted && result) {
    const pct    = Math.round((result.obtained / result.total) * 100);
    const passed = pct >= 40;
    const radius = 52;
    const circ   = 2 * Math.PI * radius;
    const dash   = (pct / 100) * circ;

    return (
      <div style={{ background: C.bg, minHeight: '100%', padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '16px', padding: '28px 32px', boxShadow: '0 1px 8px rgba(14,34,93,0.07)', display: 'flex', alignItems: 'center', gap: '32px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', width: '130px', height: '130px', flexShrink: 0 }}>
            <svg width="130" height="130" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="65" cy="65" r={radius} fill="none" stroke={C.border} strokeWidth="10" />
              <circle cx="65" cy="65" r={radius} fill="none" stroke={passed ? C.green : C.red} strokeWidth="10"
                strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '1.6rem', fontWeight: '800', color: passed ? C.green : C.red, lineHeight: 1 }}>{pct}%</span>
              <span style={{ fontSize: '0.72rem', fontWeight: '700', color: passed ? C.green : C.red, marginTop: '3px' }}>{passed ? 'PASSED' : 'FAILED'}</span>
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1.1rem', fontWeight: '800', color: C.navy, marginBottom: '14px' }}>{exam.exam_title}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {[
                { lbl: 'Score',    val: `${result.obtained} / ${result.total}`, bg: C.goldDim,  border: C.goldBorder,  color: '#a06000' },
                { lbl: 'Answered', val: String(result.answered),                bg: C.greenDim, border: C.greenBorder, color: C.green },
                { lbl: 'Skipped',  val: String(result.skipped),                 bg: C.redDim,   border: C.redBorder,   color: C.red },
              ].map(({ lbl, val, bg, border, color }) => (
                <div key={lbl} style={{ background: bg, border: `1px solid ${border}`, borderRadius: '10px', padding: '10px 16px', textAlign: 'center', minWidth: '80px' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: '800', color, lineHeight: 1 }}>{val}</div>
                  <div style={{ fontSize: '0.7rem', color, marginTop: '4px', fontWeight: '600', textTransform: 'uppercase' }}>{lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, fontWeight: '700', fontSize: '0.9rem', color: C.navy }}>
            Answer Review
          </div>
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {questions.map((q, i) => {
              const skipped     = answers[q.question_id] === undefined;
              const borderColor = skipped ? C.border : C.greenBorder;
              const bgColor     = skipped ? C.white  : C.greenDim;
              const icon        = skipped ? 'pi-minus-circle' : 'pi-check-circle';
              const iconColor   = skipped ? C.muted : C.green;
              return (
                <div key={q.question_id} style={{ border: `1px solid ${borderColor}`, borderRadius: '10px', padding: '12px 16px', background: bgColor }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <i className={`pi ${icon}`} style={{ fontSize: '1rem', color: iconColor, flexShrink: 0, marginTop: '2px' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: '700', background: 'rgba(14,34,93,0.08)', color: C.navy, padding: '2px 9px', borderRadius: '20px' }}>Q {i + 1}</span>
                        <span style={{ fontSize: '0.7rem', color: iconColor, fontWeight: '700' }}>{skipped ? 'Skipped' : 'Answered'}</span>
                      </div>
                      <p style={{ fontSize: '0.85rem', fontWeight: '600', color: C.body, lineHeight: 1.5, marginBottom: '6px' }}>{q.question_text}</p>
                      <div style={{ fontSize: '0.78rem', color: C.sub }}>
                        Your answer: <strong style={{ color: skipped ? C.muted : C.navy }}>{skipped ? '—' : answers[q.question_id]}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  /* ── EXAM SCREEN ── */
  const question = questions[currentIdx];

  return (
    <>
      <style>{`
        .ep-short { width:100%; min-height:120px; padding:12px 14px; border:1.5px solid ${C.border}; border-radius:10px; font-size:0.88rem; color:${C.body}; resize:vertical; outline:none; font-family:inherit; transition:border-color 0.15s; }
        .ep-short:focus { border-color:${C.navy}; }
        .ep-opt:hover { border-color:${C.gold} !important; background:${C.goldDim} !important; }
        .ep-dot:hover { opacity:0.8; }
        .ep-nav:hover:not(:disabled) { border-color:${C.navy} !important; color:${C.navy} !important; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: C.bg }}>

        {/* TOP BAR */}
        <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: '0 20px', height: '58px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, gap: '12px' }}>
          <div>
            <div style={{ fontWeight: '700', fontSize: '0.92rem', color: C.navy }}>{exam.exam_title}</div>
            <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: '1px' }}>
              {exam.subject} &nbsp;·&nbsp; {questions.length} Questions &nbsp;·&nbsp; {exam.total_marks} Marks
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 16px', borderRadius: '9px', background: isCritical ? C.redDim : isWarning ? '#fffbeb' : C.navyHover, border: `1px solid ${isCritical ? C.redBorder : isWarning ? '#fcd34d' : 'rgba(14,34,93,0.12)'}` }}>
            <i className="pi pi-clock" style={{ fontSize: '0.9rem', color: isCritical ? C.red : isWarning ? '#92400e' : C.navy }} />
            <div>
              <div style={{ fontSize: '10px', fontWeight: '600', color: isCritical ? C.red : isWarning ? '#92400e' : C.muted, lineHeight: 1 }}>
                {isCritical ? 'HURRY UP!' : 'TIME LEFT'}
              </div>
              <div style={{ fontSize: '1.2rem', fontWeight: '800', color: isCritical ? C.red : isWarning ? '#92400e' : C.navy, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
                {fmtTime(timeLeft)}
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowConfirm(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 20px', background: C.navy, border: 'none', borderRadius: '9px', color: C.white, fontSize: '0.83rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(14,34,93,0.25)', transition: 'all 0.2s' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = C.navyMid; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = C.navy; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <i className="pi pi-check-circle" style={{ fontSize: '0.88rem' }} /> Submit Exam
          </button>
        </div>

        {/* BODY */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* Question Area */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '0.78rem', color: C.sub, whiteSpace: 'nowrap' }}>Question {currentIdx + 1} of {questions.length}</span>
              <div style={{ flex: 1, height: '5px', background: C.border, borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ height: '100%', background: C.gold, borderRadius: '3px', width: `${((currentIdx + 1) / questions.length) * 100}%`, transition: 'width 0.3s' }} />
              </div>
              <span style={{ fontSize: '0.78rem', color: C.sub, whiteSpace: 'nowrap' }}>{answeredCount} answered</span>
            </div>

            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '22px 24px', boxShadow: '0 1px 4px rgba(14,34,93,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <span style={{ background: C.navy, color: C.white, fontSize: '0.72rem', fontWeight: '700', padding: '3px 11px', borderRadius: '20px' }}>Q {currentIdx + 1}</span>
                <span style={{ background: C.goldDim, color: '#a06000', border: `1px solid ${C.goldBorder}`, fontSize: '0.72rem', fontWeight: '700', padding: '3px 11px', borderRadius: '20px' }}>
                  {question.marks} {question.marks === 1 ? 'Mark' : 'Marks'}
                </span>
                {question.section && (
                  <span style={{ background: 'rgba(14,34,93,0.07)', color: C.navy, fontSize: '0.72rem', fontWeight: '600', padding: '3px 11px', borderRadius: '20px' }}>
                    {question.section}
                  </span>
                )}
                {flags.has(question.question_id) && (
                  <span style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d', fontSize: '0.72rem', fontWeight: '700', padding: '3px 11px', borderRadius: '20px' }}>
                    ⚑ For Review
                  </span>
                )}
              </div>

              <p style={{ fontSize: '0.95rem', fontWeight: '600', color: C.body, lineHeight: 1.65, marginBottom: '22px' }}>
                {question.question_text}
              </p>

              {(question.question_type === 'mcq' || question.question_type === 'true_false') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                  {question.options.map((opt, i) => {
                    const selected = answers[question.question_id] === opt;
                    return (
                      <button
                        key={i}
                        className="ep-opt"
                        onClick={() => setAnswer(question.question_id, opt)}
                        style={{ display: 'flex', alignItems: 'center', gap: '13px', padding: '11px 16px', textAlign: 'left', border: selected ? `1.5px solid ${C.gold}` : `1.5px solid ${C.border}`, borderRadius: '10px', cursor: 'pointer', background: selected ? C.goldDim : C.white, transition: 'all 0.15s', width: '100%' }}
                      >
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', fontWeight: '700', background: selected ? C.gold : 'transparent', border: selected ? `1.5px solid ${C.gold}` : `1.5px solid ${C.border}`, color: selected ? C.navy : C.muted }}>
                          {question.question_type === 'true_false' ? (i === 0 ? 'T' : 'F') : OPTION_LETTERS[i]}
                        </div>
                        <span style={{ fontSize: '0.88rem', color: selected ? C.body : C.sub, fontWeight: selected ? '600' : '400' }}>{opt}</span>
                        {selected && <i className="pi pi-check" style={{ marginLeft: 'auto', fontSize: '0.78rem', color: C.gold }} />}
                      </button>
                    );
                  })}
                </div>
              )}

              {question.question_type === 'short' && (
                <div>
                  <textarea
                    className="ep-short"
                    placeholder="Type your answer here…"
                    value={shortDraft}
                    onChange={(e) => setShortDraft(e.target.value)}
                    onBlur={() => { if (shortDraft.trim()) setAnswer(question.question_id, shortDraft.trim()); }}
                  />
                  <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: '6px' }}>
                    {shortDraft.trim().split(/\s+/).filter(Boolean).length} words
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <button
                className="ep-nav"
                onClick={() => goTo(Math.max(0, currentIdx - 1))}
                disabled={currentIdx === 0}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', border: `1.5px solid ${C.border}`, borderRadius: '9px', background: C.white, color: C.sub, fontSize: '0.83rem', fontWeight: '600', cursor: currentIdx === 0 ? 'not-allowed' : 'pointer', opacity: currentIdx === 0 ? 0.4 : 1, transition: 'all 0.15s' }}
              >
                <i className="pi pi-arrow-left" style={{ fontSize: '0.8rem' }} /> Previous
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {answers[question.question_id] !== undefined && (
                  <button
                    onClick={() => clearAnswer(question.question_id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '9px 14px', border: `1.5px solid ${C.redBorder}`, borderRadius: '9px', background: C.redDim, color: C.red, fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}
                  >
                    <i className="pi pi-times" style={{ fontSize: '0.75rem' }} /> Clear
                  </button>
                )}
                <button
                  onClick={() => toggleFlag(question.question_id)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 16px', border: flags.has(question.question_id) ? '1.5px solid #fcd34d' : `1.5px solid ${C.border}`, borderRadius: '9px', background: flags.has(question.question_id) ? '#fffbeb' : C.white, color: flags.has(question.question_id) ? '#92400e' : C.sub, fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' }}
                >
                  <i className="pi pi-flag" style={{ fontSize: '0.8rem' }} />
                  {flags.has(question.question_id) ? 'Marked' : 'Mark for Review'}
                </button>
              </div>

              <button
                className="ep-nav"
                onClick={() => goTo(Math.min(questions.length - 1, currentIdx + 1))}
                disabled={currentIdx === questions.length - 1}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', border: `1.5px solid ${C.border}`, borderRadius: '9px', background: C.white, color: C.sub, fontSize: '0.83rem', fontWeight: '600', cursor: currentIdx === questions.length - 1 ? 'not-allowed' : 'pointer', opacity: currentIdx === questions.length - 1 ? 0.4 : 1, transition: 'all 0.15s' }}
              >
                Next <i className="pi pi-arrow-right" style={{ fontSize: '0.8rem' }} />
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ width: '240px', borderLeft: `1px solid ${C.border}`, background: C.white, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', flexShrink: 0 }}>

            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: C.muted, marginBottom: '8px' }}>Summary</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px' }}>
                {[
                  { num: answeredCount,   lbl: 'Answered' },
                  { num: unanswered,      lbl: 'Skipped' },
                  { num: flags.size,      lbl: 'For Review' },
                  { num: questions.length, lbl: 'Total' },
                ].map(({ num, lbl }) => (
                  <div key={lbl} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 10px' }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: '800', color: C.navy, lineHeight: 1 }}>{num}</div>
                    <div style={{ fontSize: '10px', color: C.muted, marginTop: '3px' }}>{lbl}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: C.muted, marginBottom: '8px' }}>Question Palette</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '5px' }}>
                {questions.map((q, i) => (
                  <button key={q.question_id} className="ep-dot" onClick={() => goTo(i)}
                    style={{ width: '36px', height: '36px', borderRadius: '8px', border: '1.5px solid', fontSize: '11px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.12s', ...dotStyle(i) }}>
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: C.muted, marginBottom: '8px' }}>Legend</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {[
                  { bg: C.navy,     border: C.navy,    label: 'Current' },
                  { bg: C.greenDim, border: '#86efac', label: 'Answered' },
                  { bg: '#fffbeb',  border: '#fcd34d', label: 'For Review' },
                  { bg: C.white,    border: C.border,  label: 'Not Visited' },
                ].map(({ bg, border, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '11px', color: C.sub }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: bg, border: `1.5px solid ${border}`, flexShrink: 0 }} />
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Confirm Overlay */}
      {showConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: C.white, borderRadius: '16px', padding: '28px', width: '400px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontWeight: '800', fontSize: '1rem', color: C.navy, marginBottom: '18px' }}>Submit Exam?</div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px', marginBottom: '18px' }}>
              {[
                { num: answeredCount, lbl: 'Answered', bg: C.greenDim, border: C.greenBorder, color: C.green },
                { num: unanswered,    lbl: 'Skipped',  bg: C.redDim,   border: C.redBorder,   color: C.red },
                { num: flags.size,    lbl: 'Review',   bg: '#fffbeb',  border: '#fcd34d',     color: '#92400e' },
              ].map(({ num, lbl, bg, border, color }) => (
                <div key={lbl} style={{ background: bg, border: `1px solid ${border}`, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '800', color, lineHeight: 1 }}>{num}</div>
                  <div style={{ fontSize: '0.7rem', color, marginTop: '4px', fontWeight: '600' }}>{lbl}</div>
                </div>
              ))}
            </div>

            {unanswered > 0 && (
              <div style={{ display: 'flex', gap: '10px', padding: '12px 14px', background: C.redDim, border: `1px solid ${C.redBorder}`, borderRadius: '9px', marginBottom: '18px' }}>
                <i className="pi pi-exclamation-triangle" style={{ color: C.red, flexShrink: 0 }} />
                <p style={{ fontSize: '0.82rem', color: C.red, fontWeight: '500', margin: 0, lineHeight: 1.5 }}>
                  {unanswered} question{unanswered > 1 ? 's' : ''} unanswered. You cannot change answers after submitting.
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowConfirm(false)}
                style={{ padding: '9px 18px', border: `1.5px solid ${C.border}`, borderRadius: '9px', background: C.white, color: C.sub, fontSize: '0.83rem', fontWeight: '600', cursor: 'pointer' }}
              >
                Continue Exam
              </button>
              <button
                onClick={handleSubmit}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 20px', background: C.navy, border: 'none', borderRadius: '9px', color: C.white, fontSize: '0.83rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(14,34,93,0.25)' }}
              >
                <i className="pi pi-check-circle" style={{ fontSize: '0.85rem' }} /> Confirm Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TakeExam;